import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  ShieldCheck,
  Download,
  Eye,
  Check,
  ChevronDown,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import {
  BGW_CLUSTERS,
  createDefaultBgwMeasureStates,
  getMeasuresForCluster,
  type BgwClusterId,
  type BgwMeasureState,
} from '../../utils/pe-papers/bgwCatalog';
import {
  buildBgwAssessmentPdf,
  type GenerateBgwPdfParams,
} from '../../utils/pe-papers/bgwPdfGenerator';
import { openPdfBlobInNewTab } from '../../utils/pe-papers/openPdfBlob';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import PePapersStorageHint, { PE_PAPERS_STORAGE_HINTS } from './PePapersStorageHint';

export interface BgwAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  onArchived?: () => void;
}

const todayIso = () => new Date().toISOString().split('T')[0];

const formatGermanDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
};

const BgwAssessmentModal: React.FC<BgwAssessmentModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onArchived,
}) => {
  const clientData = useAppStore((s: any) => s.clientData);
  const { personalData, unproductivePersonalData, azubiAssistenzData } =
    usePersonalDataSelector();

  const [openClusterId, setOpenClusterId] = useState<BgwClusterId | null>('hautschutz');
  const [measures, setMeasures] = useState<BgwMeasureState[]>(createDefaultBgwMeasureStates);
  const [assessedAt, setAssessedAt] = useState(todayIso);
  const [confirmerName, setConfirmerName] = useState('');
  const [firstAiderName, setFirstAiderName] = useState('');
  const [fireSafetyOfficerName, setFireSafetyOfficerName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');

  useEffect(() => {
    if (!isOpen) return;
    setValidationError('');
    setArchiveFeedback('idle');
    setIsSaving(false);
    setArchiveDocId(initialData?.id || null);

    if (initialData) {
      if (Array.isArray(initialData.measures) && initialData.measures.length > 0) {
        setMeasures(initialData.measures);
      } else {
        setMeasures(createDefaultBgwMeasureStates());
      }
      setAssessedAt(String(initialData.assessedAtIso || todayIso()));
      setConfirmerName(
        String(initialData.confirmerName || clientData?.unternehmername || '').trim()
      );
      setFirstAiderName(String(initialData.firstAiderName || ''));
      setFireSafetyOfficerName(String(initialData.fireSafetyOfficerName || ''));
      setOpenClusterId((initialData.openClusterId as BgwClusterId) || 'hautschutz');
    } else {
      setMeasures(createDefaultBgwMeasureStates());
      setAssessedAt(todayIso());
      setConfirmerName(String(clientData?.unternehmername || '').trim());
      setFirstAiderName('');
      setFireSafetyOfficerName('');
      setOpenClusterId('hautschutz');
    }
  }, [isOpen, initialData, clientData?.unternehmername]);

  const salonName =
    clientData?.salonname || clientData?.firmenname || 'Salon';
  const salonOwner = clientData?.unternehmername || 'Inhaber';
  const street = String(clientData?.strasse || '').trim();
  const zip = String(clientData?.plz || '').trim();
  const city = String(clientData?.ort || '').trim();

  const teamSize = useMemo(() => {
    const count = (arr: unknown) => (Array.isArray(arr) ? arr.length : 0);
    const staff =
      count(personalData) + count(unproductivePersonalData) + count(azubiAssistenzData);
    // Leer / Solo: mindestens 1 (Inhaber)
    return Math.max(1, staff);
  }, [personalData, unproductivePersonalData, azubiAssistenzData]);

  if (!isOpen) return null;

  const setCompliant = (id: string, compliant: boolean) => {
    setMeasures((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, compliant, note: compliant ? '' : m.note }
          : m
      )
    );
    setValidationError('');
  };

  const setNote = (id: string, note: string) => {
    setMeasures((prev) =>
      prev.map((m) => (m.id === id ? { ...m, note } : m))
    );
    setValidationError('');
  };

  const confirmAllStandards = () => {
    setMeasures(createDefaultBgwMeasureStates());
    setValidationError('');
  };

  const validateBeforeExport = (): boolean => {
    const missing = measures.filter((m) => !m.compliant && !String(m.note || '').trim());
    if (missing.length > 0) {
      setValidationError(
        'Bei Abweichungen ist eine Bemerkung / geplante Maßnahme (Frist) Pflicht.'
      );
      const first = missing[0];
      const defCluster = BGW_CLUSTERS.find((c) =>
        c.measureIds.includes(first.id)
      );
      if (defCluster) setOpenClusterId(defCluster.id);
      return false;
    }
    if (!String(confirmerName || '').trim()) {
      setValidationError('Bitte die bestätigende Person angeben.');
      return false;
    }
    return true;
  };

  const buildPdfParams = (): GenerateBgwPdfParams => ({
    salonName,
    salonOwner,
    street,
    zip,
    city,
    teamSize,
    assessedAt: formatGermanDate(assessedAt),
    confirmerName: confirmerName.trim() || salonOwner,
    measures,
    firstAiderName: firstAiderName.trim() || undefined,
    fireSafetyOfficerName: fireSafetyOfficerName.trim() || undefined,
  });

  const handlePreviewPrint = () => {
    if (!validateBeforeExport()) return;
    const { blob, filename } = buildBgwAssessmentPdf(buildPdfParams());
    openPdfBlobInNewTab(blob, filename);
  };

  const handleSave = () => {
    if (!validateBeforeExport()) return;
    const { doc, filename } = buildBgwAssessmentPdf(buildPdfParams());
    doc.save(filename);
  };

  const handleArchive = async (asDraft = false) => {
    if (isSaving) return;
    if (!asDraft && !validateBeforeExport()) return;

    setIsSaving(true);
    setArchiveFeedback('idle');
    try {
      const id = await savePePapersArchiveDoc({
        id: archiveDocId,
        documentType: 'bgw-gefaehrdungsbeurteilung',
        status: asDraft ? 'entwurf' : 'final',
        salonName,
        mitarbeiterName: confirmerName.trim() || salonOwner,
        confirmedDisclaimer: !asDraft,
        payload: {
          measures,
          assessedAtIso: assessedAt,
          confirmerName: confirmerName.trim(),
          firstAiderName: firstAiderName.trim(),
          fireSafetyOfficerName: fireSafetyOfficerName.trim(),
          openClusterId,
        },
      });
      setArchiveDocId(id);
      setArchiveFeedback(asDraft ? 'draft' : 'success');
      onArchived?.();
      if (asDraft) {
        setTimeout(() => onClose(), 600);
      } else {
        setTimeout(() => setArchiveFeedback('idle'), 3000);
      }
    } catch (e) {
      console.error('BGW Archivierung fehlgeschlagen:', e);
      setArchiveFeedback('error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 p-6 text-slate-50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-400">
              § 5 ArbSchG · DGUV V2 · TRGS 530
            </p>
            <h2 className="mt-1 text-lg font-black uppercase tracking-wider text-white">
              BGW-Gefährdungsbeurteilung
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Friseurhandwerk – Standardmaßnahmen bestätigen und als DIN-A4-PDF exportieren.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-400 hover:text-white"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stammdaten read-only */}
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-xl border border-slate-700 bg-slate-800/60 p-3 sm:grid-cols-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Salon
            </p>
            <p className="text-sm font-semibold text-slate-100">{salonName}</p>
            <p className="text-xs text-slate-400">
              {[street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ') ||
                'Adresse fehlt in Stammdaten'}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Inhaber · Team
            </p>
            <p className="text-sm font-semibold text-slate-100">{salonOwner}</p>
            <p className="text-xs text-slate-400">Teamgröße: {teamSize}</p>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Prüfdatum
            </span>
            <input
              type="date"
              value={assessedAt}
              onChange={(e) => setAssessedAt(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-50 focus:border-amber-500/50 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Bestätigende Person
            </span>
            <input
              type="text"
              value={confirmerName}
              onChange={(e) => setConfirmerName(e.target.value)}
              placeholder="Name des/der Inhaber/in"
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
            />
          </label>
        </div>

        <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
          <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-400">
            Cluster 5 · Benannte Helfer (DGUV V2)
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Name Ersthelfer
              </span>
              <input
                type="text"
                value={firstAiderName}
                onChange={(e) => setFirstAiderName(e.target.value)}
                placeholder="Vor- und Nachname des Ersthelfers"
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2.5 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Name Brandschutzhelfer
              </span>
              <input
                type="text"
                value={fireSafetyOfficerName}
                onChange={(e) => setFireSafetyOfficerName(e.target.value)}
                placeholder="Vor- und Nachname des Brandschutzhelfers"
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2.5 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
              />
            </label>
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={confirmAllStandards}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-emerald-300 hover:bg-emerald-500/20"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Alle Standardmaßnahmen bestätigen
          </button>
          <button
            type="button"
            onClick={() => {
              setMeasures(createDefaultBgwMeasureStates());
              setFirstAiderName('');
              setFireSafetyOfficerName('');
              setValidationError('');
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-amber-300"
            title="Zurücksetzen"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </div>

        {/* Cluster accordion */}
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
          {BGW_CLUSTERS.map((cluster) => {
            const isOpenCluster = openClusterId === cluster.id;
            const clusterMeasures = getMeasuresForCluster(cluster.id);
            const nonCompliantCount = clusterMeasures.filter((def) => {
              const st = measures.find((m) => m.id === def.id);
              return st && !st.compliant;
            }).length;

            return (
              <div
                key={cluster.id}
                className="overflow-hidden rounded-xl border border-slate-700 bg-slate-800/40"
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenClusterId(isOpenCluster ? null : cluster.id)
                  }
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/80"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-50">{cluster.title}</p>
                    {cluster.legalRef ? (
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">
                        {cluster.legalRef}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {nonCompliantCount > 0 ? (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                        {nonCompliantCount} Abweichung
                        {nonCompliantCount === 1 ? '' : 'en'}
                      </span>
                    ) : (
                      <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
                        OK
                      </span>
                    )}
                    <ChevronDown
                      className={`h-4 w-4 text-slate-400 transition-transform ${
                        isOpenCluster ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {isOpenCluster ? (
                  <div className="space-y-2 border-t border-slate-700 px-3 py-3">
                    {clusterMeasures.map((def) => {
                      const state = measures.find((m) => m.id === def.id);
                      if (!state) return null;
                      const noteRequired = !state.compliant;

                      return (
                        <div
                          key={def.id}
                          className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-50">
                                {def.title}
                              </p>
                              {def.legalHint ? (
                                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                                  {def.legalHint}
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={state.compliant}
                              onClick={() => setCompliant(def.id, !state.compliant)}
                              className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
                                state.compliant ? 'bg-emerald-500' : 'bg-amber-600'
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform ${
                                  state.compliant ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              >
                                {state.compliant ? (
                                  <Check className="h-3 w-3 text-emerald-600" />
                                ) : null}
                              </span>
                            </button>
                          </div>

                          {def.nameFieldKey === 'firstAiderName' ? (
                            <label className="mt-2 block">
                              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Name Ersthelfer (optional)
                              </span>
                              <input
                                type="text"
                                value={firstAiderName}
                                onChange={(e) => setFirstAiderName(e.target.value)}
                                placeholder="Vor- und Nachname"
                                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
                              />
                            </label>
                          ) : null}

                          {def.nameFieldKey === 'fireSafetyOfficerName' ? (
                            <label className="mt-2 block">
                              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Name Brandschutzhelfer (optional)
                              </span>
                              <input
                                type="text"
                                value={fireSafetyOfficerName}
                                onChange={(e) => setFireSafetyOfficerName(e.target.value)}
                                placeholder="Vor- und Nachname"
                                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
                              />
                            </label>
                          ) : null}

                          {noteRequired ? (
                            <label className="mt-2 block">
                              <span className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                                <AlertTriangle className="h-3 w-3" />
                                Bemerkung / geplante Maßnahme (Pflicht)
                              </span>
                              <textarea
                                value={state.note}
                                onChange={(e) => setNote(def.id, e.target.value)}
                                rows={2}
                                placeholder="Abweichung beschreiben und Frist / Ersatzmaßnahme angeben…"
                                className="w-full resize-y rounded-lg border border-amber-500/40 bg-slate-800 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:border-amber-500/70 focus:outline-none"
                              />
                            </label>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {validationError ? (
          <p className="mt-3 flex items-center gap-2 text-xs text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {validationError}
          </p>
        ) : null}

        <p className="mt-3 text-[10px] leading-relaxed text-slate-500">
          Dokumentationshilfe für den Saloninhaber. Ersetzt keine behördliche Vor-Ort-Begehung.
        </p>

        <PePapersStorageHint
          hint={PE_PAPERS_STORAGE_HINTS.bgw}
          variant="modal"
          className="mt-4 print:hidden"
        />

        {/* Footer */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-800 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-800 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void handleArchive(true)}
              disabled={isSaving}
              className="rounded-xl border border-slate-600 bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >
              {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleArchive(false)}
              disabled={isSaving}
              className="rounded-xl border border-amber-500/40 bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-amber-300 hover:bg-slate-800 disabled:opacity-50"
            >
              {isSaving && archiveFeedback !== 'draft'
                ? 'Speichert...'
                : archiveFeedback === 'success'
                  ? 'Gespeichert! ✓'
                  : 'Im Archiv speichern'}
            </button>
            <button
              type="button"
              onClick={handlePreviewPrint}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-200 hover:border-amber-500/50 hover:bg-slate-800 hover:text-amber-300"
            >
              <Eye className="h-4 w-4" />
              Ansehen / Drucken
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-950 shadow-lg hover:bg-amber-400"
            >
              <Download className="h-4 w-4" />
              Speichern (PDF)
            </button>
          </div>
        </div>
        {archiveFeedback === 'error' ? (
          <p className="mt-2 text-center text-[10px] text-red-400">
            Archivierung fehlgeschlagen. Bitte erneut versuchen.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default BgwAssessmentModal;
